import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Plus, Trash2, Search, Sparkles, PenLine, Menu, X, ArrowDown, AlertCircle, MessageSquare, Database, Plug, CheckCircle2, XCircle, Globe, Link, ChevronDown, ChevronRight, Cpu, FileText, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// =================== API Functions ===================

const api = {
  async getConversations() {
    const res = await fetch(`${BACKEND_URL}/api/conversations`);
    if (!res.ok) throw new Error('Failed to fetch conversations');
    return res.json();
  },

  async createConversation(title) {
    const res = await fetch(`${BACKEND_URL}/api/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title || 'Nueva conversación' }),
    });
    if (!res.ok) throw new Error('Failed to create conversation');
    return res.json();
  },

  async getConversation(id) {
    const res = await fetch(`${BACKEND_URL}/api/conversations/${id}`);
    if (!res.ok) throw new Error('Failed to fetch conversation');
    return res.json();
  },

  async deleteConversation(id) {
    const res = await fetch(`${BACKEND_URL}/api/conversations/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to delete conversation');
    return res.json();
  },

  streamChat(conversationId, message, onEvent) {
    const controller = new AbortController();

    fetch(`${BACKEND_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_id: conversationId, message }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          onEvent({ type: 'error', error: 'Error de conexión con el servidor' });
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let gotDone = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                onEvent(data);
                if (data.type === 'done') gotDone = true;
              } catch (e) {
                // ignore parse errors
              }
            }
          }
        }

        // Safety: if stream ended without a 'done' event, send one
        if (!gotDone) {
          onEvent({ type: 'done' });
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          onEvent({ type: 'error', error: err.message });
        }
      });

    return controller;
  },

  async getMcpStatus() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/mcp/status`);
      if (!res.ok) return { connected: false, label: 'ClickUp Database' };
      return res.json();
    } catch {
      return { connected: false, label: 'ClickUp Database' };
    }
  },
};

// =================== MCP Badge ===================

function McpBadge({ mcpStatus }) {
  if (!mcpStatus) return null;

  return (
    <div
      data-testid="mcp-status-badge"
      className="mcp-badge"
      title={mcpStatus.connected ? 'MCP conectado - Datos de ClickUp disponibles' : 'MCP desconectado'}
    >
      <Plug size={13} />
      <span className="mcp-badge-label">MCP</span>
      <span className="mcp-badge-separator" />
      <Database size={12} />
      <span>{mcpStatus.label || 'ClickUp'}</span>
      <span className={`mcp-badge-dot ${mcpStatus.connected ? 'connected' : 'disconnected'}`} />
    </div>
  );
}

// =================== Thinking Panel (Gemini-style) ===================

const stepIcons = {
  analyze: Sparkles,
  search: Globe,
  url: Link,
  database: Database,
  delegate: Zap,
  tool: Cpu,
  result: FileText,
  write: PenLine,
};

function ThinkingPanel({ steps, isActive }) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (!steps || steps.length === 0) return null;

  return (
    <div className="thinking-panel" data-testid="thinking-panel">
      <button
        className="thinking-panel-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="thinking-panel-toggle"
      >
        <span className="thinking-panel-toggle-icon">
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <Sparkles size={14} className="thinking-panel-sparkle" />
        <span className="thinking-panel-label">
          {isActive ? 'Pensando...' : `Proceso completado · ${steps.length} pasos`}
        </span>
        {isActive && <span className="thinking-panel-spinner" />}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            className="thinking-steps"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {steps.map((step, i) => {
              const Icon = stepIcons[step.step] || Sparkles;
              const isLatest = i === steps.length - 1 && isActive;
              return (
                <motion.div
                  key={i}
                  className={`thinking-step ${isLatest ? 'active' : ''}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15, delay: 0.02 }}
                >
                  <div className="thinking-step-line">
                    <div className={`thinking-step-dot ${isLatest ? 'active' : 'done'}`}>
                      <Icon size={11} />
                    </div>
                    {i < steps.length - 1 && <div className="thinking-step-connector" />}
                  </div>
                  <div className="thinking-step-content">
                    <span className="thinking-step-title">{step.title}</span>
                    {step.detail && (
                      <span className="thinking-step-detail">{step.detail}</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =================== Status Indicator ===================

function StatusIndicator({ status, detail }) {
  const statusConfig = {
    thinking: { icon: Sparkles, label: 'Pensando...', color: 'hsl(var(--brand))' },
    searching: { icon: Search, label: 'Buscando en fuentes...', color: 'hsl(var(--brand-2))' },
    writing: { icon: PenLine, label: 'Escribiendo...', color: 'hsl(var(--brand))' },
  };

  const config = statusConfig[status] || statusConfig.thinking;
  const Icon = config.icon;

  return (
    <motion.div
      data-testid="agent-status-indicator"
      className="status-indicator"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.15 }}
    >
      <span className="status-dot" />
      <Icon size={14} style={{ color: config.color }} />
      <span data-testid="agent-status-text">{detail || config.label}</span>
    </motion.div>
  );
}

// =================== Empty State ===================

function EmptyState({ onPromptClick, mcpStatus }) {
  const prompts = [
    '¿Cuántos miembros tiene el equipo?',
    '¿Cuáles son las tareas pendientes?',
    'Resumen de productividad del equipo',
  ];

  return (
    <div className="empty-state" data-testid="chat-empty-state">
      <Database size={48} style={{ color: 'hsl(var(--brand))', marginBottom: '1rem' }} />
      <h2>¿Qué quieres analizar hoy?</h2>
      <p>Pregunta por el equipo, tareas, productividad o cualquier métrica de ClickUp.</p>
      {mcpStatus && (
        <div className="empty-state-mcp" data-testid="empty-state-mcp">
          <div className={`mcp-connection-card ${mcpStatus.connected ? 'connected' : 'disconnected'}`}>
            <div className="mcp-connection-icon">
              <Plug size={18} />
            </div>
            <div className="mcp-connection-info">
              <span className="mcp-connection-title">MCP · {mcpStatus.label || 'ClickUp Database'}</span>
              <span className="mcp-connection-status">
                {mcpStatus.connected ? (
                  <><CheckCircle2 size={12} /> Conectado</>
                ) : (
                  <><XCircle size={12} /> Desconectado</>
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="starter-cards">
        {prompts.map((prompt, i) => (
          <button
            key={i}
            className="starter-card"
            data-testid="starter-prompt-card"
            onClick={() => onPromptClick(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>
      <div className="chat-disclaimer">
        <strong>¿Necesitas acceso?</strong> Contacta al admin de este experimento: <strong>George</strong>.
      </div>
    </div>
  );
}

// =================== Message Bubble ===================

function MessageBubble({ message, isStreaming, agentStatus, thinkingSteps }) {
  const isUser = message.role === 'user';

  return (
    <motion.div
      className={`message-row ${isUser ? 'user' : ''}`}
      data-testid={isUser ? 'user-message' : 'assistant-message'}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      {!isUser && (
        <div className="message-avatar assistant">
          <Database size={16} />
        </div>
      )}
      <div className={`message-bubble ${isUser ? 'user' : 'assistant'}`} data-testid="chat-message-text">
        {isUser ? (
          <span>{message.content}</span>
        ) : (
          <div className="chat-markdown">
            {/* Thinking panel - shows all steps */}
            {thinkingSteps && thinkingSteps.length > 0 && (
              <ThinkingPanel steps={thinkingSteps} isActive={isStreaming && !message.content} />
            )}
            {/* Content */}
            {message.content && (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            )}
            {/* Blinking cursor while writing */}
            {isStreaming && message.content && (
              <span className="inline-block w-1.5 h-4 bg-current opacity-60 animate-pulse ml-0.5" />
            )}
          </div>
        )}
      </div>
      {isUser && (
        <div className="message-avatar user-avatar">
          <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>Tú</span>
        </div>
      )}
    </motion.div>
  );
}

// =================== Main Chat Page ===================

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [mcpStatus, setMcpStatus] = useState(null);
  const [thinkingSteps, setThinkingSteps] = useState([]);

  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const textareaRef = useRef(null);
  const abortRef = useRef(null);
  const streamingTextRef = useRef('');

  // Load conversations and MCP status on mount
  useEffect(() => {
    loadConversations();
    api.getMcpStatus().then(setMcpStatus);
  }, []);

  const loadConversations = async () => {
    try {
      const convs = await api.getConversations();
      setConversations(convs);
    } catch (e) {
      console.error('Failed to load conversations:', e);
    }
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isStreaming) {
      scrollToBottom();
    }
  }, [messages, isStreaming, scrollToBottom]);

  // Handle scroll position
  const handleScroll = useCallback(() => {
    const el = messagesAreaRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollBtn(!isNearBottom && messages.length > 3);
  }, [messages.length]);

  // Load conversation messages
  const loadConversation = async (convId) => {
    try {
      setActiveConversationId(convId);
      setError(null);
      const conv = await api.getConversation(convId);
      setMessages(conv.messages || []);
      setMobileMenuOpen(false);
      setTimeout(scrollToBottom, 100);
    } catch (e) {
      setError('Error al cargar la conversación');
    }
  };

  // Create new conversation
  const handleNewConversation = async () => {
    try {
      setError(null);
      const conv = await api.createConversation();
      setConversations((prev) => [conv, ...prev]);
      setActiveConversationId(conv.id);
      setMessages([]);
      setMobileMenuOpen(false);
    } catch (e) {
      setError('Error al crear conversación');
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    try {
      await api.deleteConversation(convId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (activeConversationId === convId) {
        setActiveConversationId(null);
        setMessages([]);
      }
    } catch (e) {
      console.error('Failed to delete:', e);
    }
  };

  // Send message
  const handleSend = async (text) => {
    const messageText = text || inputValue.trim();
    if (!messageText || isStreaming) return;

    setError(null);

    // Create conversation if none active
    let convId = activeConversationId;
    if (!convId) {
      try {
        const conv = await api.createConversation();
        setConversations((prev) => [conv, ...prev]);
        convId = conv.id;
        setActiveConversationId(conv.id);
      } catch (e) {
        setError('Error al crear conversación');
        return;
      }
    }

    // Add user message to UI
    const userMsg = { role: 'user', content: messageText, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsStreaming(true);
    setAgentStatus({ status: 'thinking', detail: 'Pensando...' });
    streamingTextRef.current = '';
    setThinkingSteps([]);

    // Add placeholder assistant message
    const assistantMsg = { role: 'assistant', content: '', timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, assistantMsg]);

    // Start streaming
    abortRef.current = api.streamChat(convId, messageText, (event) => {
      switch (event.type) {
        case 'thinking_step':
          setThinkingSteps((prev) => [...prev, {
            step: event.step,
            title: event.title,
            detail: event.detail,
            expanded_detail: event.expanded_detail,
          }]);
          break;

        case 'status':
          setAgentStatus({ status: event.status, detail: event.detail });
          break;

        case 'content':
          setAgentStatus({ status: 'writing', detail: 'Escribiendo...' });
          streamingTextRef.current += event.text;
          const currentText = streamingTextRef.current;
          setMessages((prev) => {
            const updated = [...prev];
            const lastMsg = updated[updated.length - 1];
            if (lastMsg && lastMsg.role === 'assistant') {
              updated[updated.length - 1] = { ...lastMsg, content: currentText };
            }
            return updated;
          });
          break;

        case 'title_update':
          setConversations((prev) =>
            prev.map((c) => (c.id === convId ? { ...c, title: event.title } : c))
          );
          break;

        case 'error':
          setError(event.error);
          setIsStreaming(false);
          setAgentStatus(null);
          break;

        case 'done':
          setIsStreaming(false);
          setAgentStatus(null);
          loadConversations(); // Refresh sidebar
          break;

        default:
          break;
      }
    });

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Handle keyboard
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleTextareaInput = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
  };

  // Format relative time
  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const date = new Date(isoStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="app-shell">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`} data-testid="sidebar">
        <div className="sidebar-header">
          <h1>Asistente</h1>
          <button
            data-testid="new-conversation-button"
            onClick={handleNewConversation}
            style={{
              background: 'hsl(var(--primary))',
              color: 'hsl(var(--primary-foreground))',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Nueva conversación"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="sidebar-list" data-testid="conversation-history-list">
          {conversations.length === 0 ? (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
              <MessageSquare size={24} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
              <p>Sin conversaciones aún</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`sidebar-item ${activeConversationId === conv.id ? 'active' : ''}`}
                data-testid="conversation-history-item"
                onClick={() => loadConversation(conv.id)}
              >
                <div className="sidebar-item-content">
                  <div className="sidebar-item-title">{conv.title}</div>
                  <div className="sidebar-item-meta">
                    {formatTime(conv.updated_at)} · {conv.message_count || 0} msgs
                  </div>
                </div>
                <button
                  className="sidebar-item-delete"
                  onClick={(e) => handleDeleteConversation(e, conv.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    color: 'hsl(var(--muted-foreground))',
                  }}
                  title="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main Chat Area */}
      <main className="main-chat">
        {/* Chat Header */}
        <div className="chat-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem',
                display: 'flex',
                color: 'hsl(var(--foreground))',
              }}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <span className="chat-header-title">
              {activeConversationId
                ? conversations.find((c) => c.id === activeConversationId)?.title || 'Chat'
                : 'Asistente Databricks'}
            </span>
          </div>
          <McpBadge mcpStatus={mcpStatus} />
        </div>

        {/* Messages */}
        <div
          className="messages-area"
          ref={messagesAreaRef}
          onScroll={handleScroll}
          style={{ position: 'relative' }}
        >
          {messages.length === 0 && !activeConversationId ? (
            <EmptyState onPromptClick={(prompt) => handleSend(prompt)} mcpStatus={mcpStatus} />
          ) : messages.length === 0 ? (
            <EmptyState onPromptClick={(prompt) => handleSend(prompt)} mcpStatus={mcpStatus} />
          ) : (
            <div className="messages-container">
              {messages.map((msg, i) => (
                <MessageBubble
                  key={`${i}-${msg.role}`}
                  message={msg}
                  isStreaming={isStreaming && i === messages.length - 1 && msg.role === 'assistant'}
                  agentStatus={isStreaming && i === messages.length - 1 && msg.role === 'assistant' ? agentStatus : null}
                  thinkingSteps={i === messages.length - 1 && msg.role === 'assistant' ? thinkingSteps : null}
                />
              ))}
              {/* Error banner */}
              {error && (
                <div className="error-banner" data-testid="error-banner">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                  <button onClick={() => setError(null)}>Cerrar</button>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Scroll to bottom */}
          <AnimatePresence>
            {showScrollBtn && (
              <motion.button
                className="scroll-bottom-btn"
                onClick={scrollToBottom}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <ArrowDown size={14} style={{ marginRight: '0.25rem' }} /> Volver al final
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Composer */}
        <div className="composer-dock">
          <div className="composer-inner">
            <div className="composer-wrap">
              <textarea
                ref={textareaRef}
                data-testid="chat-composer-input"
                className="composer-textarea"
                placeholder="Escribe tu pregunta..."
                value={inputValue}
                onChange={handleTextareaInput}
                onKeyDown={handleKeyDown}
                rows={1}
                aria-label="Escribe tu pregunta"
                disabled={isStreaming}
              />
              <button
                data-testid="chat-composer-send-button"
                className="composer-send"
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isStreaming}
                title="Enviar mensaje"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="composer-hint">Enter para enviar · Shift+Enter nueva línea</div>
          </div>
        </div>
      </main>
    </div>
  );
}
